/**
 * Various types of checks for rota shifts.
 * These checks are shared for use in the rota grid editor, and via CheckRotaForIssues in rota-functions.ts,
 * so it is important that these tests are lightweight and don't return any shift data..
 */
import {
  RotaShiftAssignmentTypes,
  type RotaScheduleItem,
  type RotaShift,
} from "@/types";

export const ShiftCheckCode = {
  CARE_SCHEDULE: "CARE_SCHEDULE",
  OLD_SHIFT: "OLD_SHIFT",
  SHIFT_OVERLAP: "SHIFT_OVERLAP",
} as const;

export type ShiftCheckResult =
  | {
      code: string;
      message: string;
      severity: "error" | "warning" | "info";
    }
  | false;

// look at the scheulde item's notes and extract anything that looks like someone's intials - two characters in uppercase.
export const shiftHasCareWithInitials = (
  shift: RotaShift<string>,
): ShiftCheckResult => {
  if ((shift.schedule ?? []).some((item) => scheduleIsCareWithInitials(item))) {
    return {
      code: ShiftCheckCode.CARE_SCHEDULE,
      message: `Shift has a schedule with 'care' alongside what looks like client initials. Consider using 'care' activities for those clients instead.`,
      severity: "warning",
    };
  }
  return false;
};

export const scheduleIsCareWithInitials = (
  scheduleItem: RotaScheduleItem,
): boolean => {
  if (scheduleItem.type !== "care") {
    return false;
  }
  // remove common non-initials uppercase words like AM, PM, IS, OR, TO, AS
  let sanitisedNotes = (scheduleItem.notes ?? "").replaceAll(
    /\W(AM|PM|IS|OR|TO|AS)\W/g,
    "",
  ); // mid string
  sanitisedNotes = sanitisedNotes.replaceAll(/^(AM|PM)\W/g, ""); // start of string
  sanitisedNotes = sanitisedNotes.replaceAll(/\W(AM|PM)$\W/g, ""); // end of string
  if (/^([A-Z]{2})\W|\W([A-Z]{2})$|\W([A-Z]{2})\W/.test(sanitisedNotes)) {
    return true;
  }
  return false;
};

export const shiftIsOld = (shift: RotaShift<string>): ShiftCheckResult => {
  if (shift.assignedTo === undefined || shift.assignedTo.length === 0) {
    return false;
  }

  if (shift.assignedTo.at(-1)!.type == RotaShiftAssignmentTypes.OLD) {
    return {
      code: ShiftCheckCode.OLD_SHIFT,
      message: `This shift is marked as old and could be removed`,
      severity: "info",
    };
  }

  return false;
};
